import { useEffect, useState } from 'react'
import {
  subjectProperty,
  doubleBayComparables,
  daComparison,
  leaseTerms,
  nearbySchools,
  demographics,
} from '@/data/properties'

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [enteredRate, setEnteredRate] = useState<number | null>(null)

  // Constants
  const SQM = 177
  const MARKET_RATE = 2443
  const MARKET_INCREASE = 0.036
  const ENTERED_INCREASE = 0.035

  // Calculations based on entered rate
  const monthlyRent = enteredRate ? Math.round((enteredRate * SQM) / 12) : null
  const marketMonthly = Math.round((MARKET_RATE * SQM) / 12)
  const percentBelow = enteredRate ? Math.round(((MARKET_RATE - enteredRate) / MARKET_RATE) * 100) : null

  // Calculate compound totals
  const calcTotal = (baseRate: number, increase: number, years: number) => {
    let total = 0
    let annual = baseRate * SQM
    for (let i = 0; i < years; i++) {
      total += annual
      annual *= (1 + increase)
    }
    return total
  }

  const market5Year = calcTotal(MARKET_RATE, MARKET_INCREASE, 5)
  const market10Year = calcTotal(MARKET_RATE, MARKET_INCREASE, 10)
  const entered5Year = enteredRate ? calcTotal(enteredRate, ENTERED_INCREASE, 5) : null
  const entered10Year = enteredRate ? calcTotal(enteredRate, ENTERED_INCREASE, 10) : null
  const savings5Year = enteredRate ? market5Year - entered5Year! : null
  const savings10Year = enteredRate ? market10Year - entered10Year! : null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (password.toLowerCase() === atob('ZGhhcm1h')) {
      setIsAuthenticated(true)
      setError(false)
    } else {
      setError(true)
      setPassword('')
    }
  }

  useEffect(() => {
    if (!isAuthenticated) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0')
            entry.target.classList.remove('opacity-0', 'translate-y-8')
          }
        })
      },
      { threshold: 0.1 }
    )
    document.querySelectorAll('.fade-in').forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [isAuthenticated])

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black text-white antialiased flex items-center justify-center">
        <div className="w-full max-w-sm px-6">
          <div className="text-center mb-12">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-b from-gray-700 to-gray-900 flex items-center justify-center ring-1 ring-white/10">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight mb-2">Property Brief</h1>
            <p className="text-gray-500 text-sm">Enter password to continue</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="relative mb-4">
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                className={`w-full px-4 py-3 bg-[#1d1d1f] rounded-xl text-white placeholder-gray-500 outline-none transition-all ${
                  error
                    ? 'ring-2 ring-red-500/50'
                    : 'ring-1 ring-white/10 focus:ring-2 focus:ring-white/20'
                }`}
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center mb-4">Incorrect password</p>
            )}

            <button
              type="submit"
              className="w-full py-3 bg-white text-black font-medium rounded-xl hover:bg-gray-100 transition-colors"
            >
              Continue
            </button>
          </form>

          <p className="text-center text-gray-600 text-xs mt-8">
            2 Short Street, Double Bay
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white antialiased">
      {/* Hero */}
      <section className="min-h-[80vh] flex flex-col justify-center items-center text-center px-6 py-32">
        <div className="fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\]">
          <p className="text-gray-500 text-sm tracking-widest uppercase mb-6">Property Brief</p>
          <h1 className="text-5xl md:text-7xl font-semibold tracking-tight mb-6">
            2 Short Street
          </h1>
          <p className="text-2xl md:text-3xl text-gray-400 font-light">
            Double Bay NSW 2028
          </p>
        </div>
      </section>

      {/* DA Comparison */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\]">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              Side By Side
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\] delay-200">
            {/* Bondi */}
            <div className="bg-[#1d1d1f] rounded-3xl p-10">
              <h3 className="text-2xl font-semibold mb-8">Bondi</h3>
              <p className="text-gray-500 text-sm mb-8">Current location</p>

              <div className="space-y-6">
                <div className="flex justify-between items-baseline border-b border-white/10 pb-4">
                  <span className="text-gray-400">DA Status</span>
                  <span className="text-orange-400 font-medium">{daComparison.bondi.status}</span>
                </div>
                <div className="flex justify-between items-baseline border-b border-white/10 pb-4">
                  <span className="text-gray-400">DA Reference</span>
                  <span className="text-orange-400 font-medium">Nil</span>
                </div>
                <div className="flex justify-between items-baseline border-b border-white/10 pb-4">
                  <span className="text-gray-400">Rent</span>
                  <span className="text-white">{daComparison.bondi.rent}</span>
                </div>
                <div className="flex justify-between items-baseline border-b border-white/10 pb-4">
                  <span className="text-gray-400">Risk</span>
                  <span className="text-orange-400 font-medium">{daComparison.bondi.risk}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-400">Council</span>
                  <span className="text-white">{daComparison.bondi.council}</span>
                </div>
              </div>
            </div>

            {/* Double Bay */}
            <div className="bg-gradient-to-b from-[#1a2a1a] to-[#1d1d1f] rounded-3xl p-10 ring-1 ring-green-500/20">
              <h3 className="text-2xl font-semibold mb-8">Double Bay</h3>
              <p className="text-green-400 text-sm mb-8">Available</p>

              <div className="space-y-6">
                <div className="flex justify-between items-baseline border-b border-white/10 pb-4">
                  <span className="text-gray-400">DA Status</span>
                  <span className="text-green-400 font-medium">{daComparison.doubleBay.status}</span>
                </div>
                <div className="flex justify-between items-baseline border-b border-white/10 pb-4">
                  <span className="text-gray-400">DA Reference</span>
                  <span className="text-white">{daComparison.doubleBay.reference}</span>
                </div>
                <div className="flex justify-between items-baseline border-b border-white/10 pb-4">
                  <span className="text-gray-400">Rent</span>
                  <span className="text-green-400 font-medium">{monthlyRent ? `$${monthlyRent.toLocaleString()}/mo` : '—'}</span>
                </div>
                <div className="flex justify-between items-baseline border-b border-white/10 pb-4">
                  <span className="text-gray-400">Risk</span>
                  <span className="text-green-400 font-medium">None</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-400">Council</span>
                  <span className="text-white">{daComparison.doubleBay.council}</span>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-500 mt-12 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\] delay-300">
            {daComparison.option}
          </p>
        </div>
      </section>

      {/* Property */}
      <section className="py-24 px-6 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\]">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              The property.
            </h2>
            <p className="text-gray-500 mt-4">enter $ per sqm for page calculation</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\] delay-200">
            <div className="bg-[#1d1d1f] rounded-3xl p-8 text-center">
              <div className="text-4xl font-light text-white mb-2">{subjectProperty.tenancyArea}</div>
              <div className="text-gray-500">sqm</div>
            </div>
            <div className="bg-gradient-to-b from-[#1a2a1a] to-[#1d1d1f] rounded-3xl p-8 text-center ring-1 ring-green-500/20">
              <div className="flex items-center justify-center mb-2">
                <span className="text-4xl font-light text-green-400 mr-1">$</span>
                <input
                  type="number"
                  value={enteredRate || ''}
                  onChange={(e) => setEnteredRate(e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="0"
                  className="w-28 text-4xl font-light text-green-400 bg-transparent border-b-2 border-green-500/30 focus:border-green-400 outline-none text-center placeholder-green-400/30"
                />
              </div>
              <div className="text-green-400/70">per sqm</div>
            </div>
            <div className="bg-[#1d1d1f] rounded-3xl p-8 text-center">
              <div className="text-4xl font-light text-white mb-2">{subjectProperty.daReference}</div>
              <div className="text-gray-500">DA approved</div>
            </div>
          </div>
        </div>
      </section>

      {/* Lease Terms */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\]">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              The terms.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\] delay-200">
            <div className="space-y-6">
              {[
                { label: 'Term', value: '10Y' },
                { label: 'Term 1 (No Demo)', value: '5Y' },
                { label: 'Term 2 (Option)', value: '5Y' },
                { label: 'Annual Escalation', value: leaseTerms.escalation },
                { label: 'Discount', value: percentBelow ? `${percentBelow}% below market` : '—' },
                { label: 'Make Good', value: leaseTerms.makeGood },
              ].map((item) => (
                <div key={item.label} className="flex justify-between items-baseline border-b border-white/10 pb-4">
                  <span className="text-gray-400">{item.label}</span>
                  <span className="text-white">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-b from-[#1a2a1a] to-[#1d1d1f] rounded-3xl p-10 ring-1 ring-green-500/20">
              <div className="text-center">
                <div className="text-6xl font-light text-green-400 mb-2">
                  {monthlyRent ? `$${monthlyRent.toLocaleString()}` : '—'}
                </div>
                <div className="text-gray-400 mb-8">per month</div>

                <div className="border-t border-white/10 pt-8 space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Avg Market</span>
                    <span className="text-gray-500 line-through">${marketMonthly.toLocaleString()}/mo</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Below Market</span>
                    <span className="text-green-400">{percentBelow ? `${percentBelow}% below` : '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 5-Year Comparison */}
          <div className="mt-16 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\] delay-300">
            <h3 className="text-2xl font-semibold text-center mb-8">5-Year Comparison</h3>
            <p className="text-gray-500 text-center text-sm mb-8">177 sqm with annual increases compounded</p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-[#1d1d1f] rounded-2xl p-8 text-center">
                <div className="text-gray-500 text-sm mb-2">Market Total</div>
                <div className="text-gray-400 text-xs mb-4">$2,443/sqm + 3.6% p.a.</div>
                <div className="text-3xl font-light text-white line-through opacity-50">${(market5Year / 1000000).toFixed(2)}M</div>
              </div>

              <div className="bg-gradient-to-b from-[#1a2a1a] to-[#1d1d1f] rounded-2xl p-8 text-center ring-1 ring-green-500/20">
                <div className="text-green-400 text-sm mb-2">2 Short St</div>
                <div className="text-gray-400 text-xs mb-4">{enteredRate ? `$${enteredRate.toLocaleString()}/sqm` : '—'} + 3.5% p.a.</div>
                <div className="text-3xl font-light text-green-400">{entered5Year ? `$${(entered5Year / 1000000).toFixed(2)}M` : '—'}</div>
              </div>

              <div className="bg-gradient-to-b from-[#1a2a1a] to-[#1d1d1f] rounded-2xl p-8 text-center ring-1 ring-green-500/20">
                <div className="text-green-400 text-sm mb-2">5-Year Savings</div>
                <div className="text-gray-400 text-xs mb-4">Total difference</div>
                <div className="text-3xl font-light text-green-400">{savings5Year ? `$${(savings5Year / 1000000).toFixed(2)}M` : '—'}</div>
              </div>
            </div>
          </div>

          {/* 10-Year Comparison */}
          <div className="mt-12 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\] delay-400">
            <h3 className="text-2xl font-semibold text-center mb-8">10-Year Comparison</h3>
            <p className="text-gray-500 text-center text-sm mb-8">177 sqm with annual increases compounded</p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-[#1d1d1f] rounded-2xl p-8 text-center">
                <div className="text-gray-500 text-sm mb-2">Market Total</div>
                <div className="text-gray-400 text-xs mb-4">$2,443/sqm + 3.6% p.a.</div>
                <div className="text-3xl font-light text-white line-through opacity-50">${(market10Year / 1000000).toFixed(2)}M</div>
              </div>

              <div className="bg-gradient-to-b from-[#1a2a1a] to-[#1d1d1f] rounded-2xl p-8 text-center ring-1 ring-green-500/20">
                <div className="text-green-400 text-sm mb-2">2 Short St</div>
                <div className="text-gray-400 text-xs mb-4">{enteredRate ? `$${enteredRate.toLocaleString()}/sqm` : '—'} + 3.5% p.a.</div>
                <div className="text-3xl font-light text-green-400">{entered10Year ? `$${(entered10Year / 1000000).toFixed(2)}M` : '—'}</div>
              </div>

              <div className="bg-gradient-to-b from-[#1a2a1a] to-[#1d1d1f] rounded-2xl p-8 text-center ring-1 ring-green-500/20">
                <div className="text-green-400 text-sm mb-2">10-Year Savings</div>
                <div className="text-gray-400 text-xs mb-4">Total difference</div>
                <div className="text-3xl font-light text-green-400">{savings10Year ? `$${(savings10Year / 1000000).toFixed(2)}M` : '—'}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Market Actuals */}
      <section className="py-24 px-6 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\]">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              Double Bay Comps
            </h2>
          </div>

          {(() => {
            const avgRate = Math.round(doubleBayComparables.reduce((sum, c) => sum + c.rate, 0) / doubleBayComparables.length)
            const avgSqm = Math.round(doubleBayComparables.reduce((sum, c) => sum + c.sqm, 0) / doubleBayComparables.length)
            const increases = doubleBayComparables.filter(c => c.increase !== 'na').map(c => parseFloat(c.increase))
            const avgIncrease = (increases.reduce((sum, i) => sum + i, 0) / increases.length).toFixed(1)
            return (
              <div className="grid md:grid-cols-3 gap-6 mb-12 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\] delay-100">
                <div className="bg-[#1d1d1f] rounded-2xl p-6 text-center">
                  <div className="text-3xl font-light text-white mb-1">${avgRate.toLocaleString()}</div>
                  <div className="text-gray-500 text-sm">Avg $/sqm</div>
                </div>
                <div className="bg-[#1d1d1f] rounded-2xl p-6 text-center">
                  <div className="text-3xl font-light text-white mb-1">{avgSqm}</div>
                  <div className="text-gray-500 text-sm">Avg Sqm</div>
                </div>
                <div className="bg-[#1d1d1f] rounded-2xl p-6 text-center">
                  <div className="text-3xl font-light text-white mb-1">{avgIncrease}%</div>
                  <div className="text-gray-500 text-sm">Avg Increase</div>
                </div>
              </div>
            )
          })()}

          <div className="fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\] delay-200 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="py-3 px-3 text-left text-gray-400 font-medium">Tenant</th>
                  <th className="py-3 px-3 text-right text-gray-400 font-medium">Sqm</th>
                  <th className="py-3 px-3 text-right text-gray-400 font-medium">Base Rent</th>
                  <th className="py-3 px-3 text-right text-gray-400 font-medium">$/sqm</th>
                  <th className="py-3 px-3 text-right text-gray-400 font-medium">Outgoings</th>
                  <th className="py-3 px-3 text-right text-gray-400 font-medium">Gross Rent</th>
                  <th className="py-3 px-3 text-right text-gray-400 font-medium">$/sqm Gross</th>
                  <th className="py-3 px-3 text-left text-gray-400 font-medium">Term</th>
                  <th className="py-3 px-3 text-left text-gray-400 font-medium">Expiry</th>
                  <th className="py-3 px-3 text-left text-gray-400 font-medium">Length</th>
                  <th className="py-3 px-3 text-right text-gray-400 font-medium">Increase</th>
                </tr>
              </thead>
              <tbody>
                {doubleBayComparables.map((comp, i) => (
                  <tr key={comp.tenant + i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-3 px-3 text-white whitespace-nowrap">{comp.tenant}</td>
                    <td className="py-3 px-3 text-gray-400 text-right">{comp.sqm}</td>
                    <td className="py-3 px-3 text-white text-right">${comp.baseRent.toLocaleString()}</td>
                    <td className="py-3 px-3 text-white text-right">${comp.rate.toLocaleString()}</td>
                    <td className="py-3 px-3 text-gray-400 text-right">${comp.outgoings.toLocaleString()}</td>
                    <td className="py-3 px-3 text-white text-right">${comp.grossRent.toLocaleString()}</td>
                    <td className="py-3 px-3 text-white text-right">${comp.grossRate.toLocaleString()}</td>
                    <td className="py-3 px-3 text-gray-500">{comp.leaseStart}</td>
                    <td className="py-3 px-3 text-gray-500">{comp.expiry}</td>
                    <td className="py-3 px-3 text-gray-400">{comp.termLength}</td>
                    <td className="py-3 px-3 text-gray-400 text-right">{comp.increase}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Private Schools */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\]">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">
              Schools
            </h2>
            <p className="text-gray-500 text-lg">(inside 5km 2 Short St)</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 fade-in opacity-0 translate-y-8 transition-all duration-\[1200ms\] delay-200">
            {nearbySchools.map((school, i) => (
              <div key={school.name} className="bg-[#1d1d1f] rounded-2xl p-5">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-white font-medium text-sm">{school.name}</h3>
                  <span className="text-green-400 text-xs font-medium whitespace-nowrap ml-2">{school.distance} km</span>
                </div>
                <p className="text-gray-500 text-xs mb-2">{school.address}</p>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-xs">{school.type}</span>
                  <span className="text-gray-600 text-xs">{school.drive}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demographics */}
      <section className="py-24 px-6 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 fade-in opacity-0 translate-y-8 transition-all duration-[1200ms]">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              Demographics
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 fade-in opacity-0 translate-y-8 transition-all duration-[1200ms] delay-200">
            {/* Bondi */}
            <div className="bg-[#1d1d1f] rounded-2xl p-6 opacity-60">
              <h3 className="text-lg font-medium text-gray-400 mb-6">{demographics.bondi.name}</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-gray-500 text-xs mb-1">HH Income (Median)</div>
                  <div className="text-white text-xl font-light">{demographics.bondi.hhIncome}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs mb-1">Av Net Worth</div>
                  <div className="text-white text-xl font-light">{demographics.bondi.netWorth}</div>
                </div>
              </div>
              <a href={demographics.bondi.source} target="_blank" rel="noopener noreferrer" className="text-gray-600 text-xs mt-4 block hover:text-gray-400 transition-colors">
                {demographics.bondi.sourceLabel}
              </a>
            </div>

            {/* Double Bay */}
            <div className="bg-gradient-to-b from-[#1a2a1a] to-[#1d1d1f] rounded-2xl p-6 ring-1 ring-green-500/20">
              <h3 className="text-lg font-medium text-green-400 mb-6">{demographics.doubleBay.name}</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-gray-500 text-xs mb-1">HH Income (Median)</div>
                  <div className="text-green-400 text-xl font-light">{demographics.doubleBay.hhIncome}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs mb-1">Av Net Worth</div>
                  <div className="text-green-400 text-xl font-light">{demographics.doubleBay.netWorth}</div>
                </div>
              </div>
              <a href={demographics.doubleBay.source} target="_blank" rel="noopener noreferrer" className="text-gray-600 text-xs mt-4 block hover:text-gray-400 transition-colors">
                {demographics.doubleBay.sourceLabel}
              </a>
            </div>

            {/* Darlinghurst */}
            <div className="bg-[#1d1d1f] rounded-2xl p-6 opacity-60">
              <h3 className="text-lg font-medium text-gray-400 mb-6">{demographics.darlinghurst.name}</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-gray-500 text-xs mb-1">HH Income (Median)</div>
                  <div className="text-white text-xl font-light">{demographics.darlinghurst.hhIncome}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs mb-1">Av Net Worth</div>
                  <div className="text-white text-xl font-light">{demographics.darlinghurst.netWorth}</div>
                </div>
              </div>
              <a href={demographics.darlinghurst.source} target="_blank" rel="noopener noreferrer" className="text-gray-600 text-xs mt-4 block hover:text-gray-400 transition-colors">
                {demographics.darlinghurst.sourceLabel}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 border-t border-white/10">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-gray-600 text-sm">
            DA 142/2019 · Woollahra Municipal Council · Approved 17 June 2019
          </p>
        </div>
      </footer>
    </div>
  )
}

export default App
